import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext.js";
import { useTranslation } from "react-i18next";

import worldTourBackground from "../medias/png/background/world_tour_background.png";
import neoProBackground from "../medias/png/background/neo_pro_background.png";

import SelectActualCyclists from "./changeStep/SelectActualCyclists.js";
import SelectNewCyclists from "./changeStep/SelectNewCyclists.js";
import SelectNewCyclistsBonus from "./changeStep/SelectNewCyclistsBonus.js";
import SelectRaces from "./createStep/SelectRaces";
import SelectBonusRace from "./changeStep/SelectBonusRace";

const ChangeTeamPage = () => {
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  const [budget, setBudget] = useState();
  const [numberPlayerSelected, setNumberPlayerSelected] = useState(0);
  const [races, setRaces] = useState([]);
  const [selectedRaces, setSelectedRaces] = useState([]);
  const [bonusRaces, setBonusRaces] = useState([]);
  const [selectedCyclists, setSelectedCyclists] = useState([]);
  const [selectedActualCyclists, setSelectedActualCyclists] = useState([]);
  const [selectedNewCyclists, setSelectedNewCyclists] = useState([]);
  const [cyclistsBonus, setCyclistsBonus] = useState([]);
  const [cyclists, setCyclists] = useState([]);
  const [teamId, setTeamId] = useState();
  const [modificationTeam1Available, setModificationTeam1Available] =
    useState(false);
  const [modificationTeam2Available, setModificationTeam2Available] =
    useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  const modifyTeam = (newTeamId) => {
    setTeamId(newTeamId);
    nextStep();
  };

  const nextStep = () => {
    if (step < 6) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const saveData = () => {
    return new Promise((resolve, reject) => {
      if (!user) {
        navigate("/logIn");
        reject(t("changeTeam.errors.notConnected"));
        return;
      }

      const obligatoryRaces = races.filter(
        (race) => race.groupCompetitionId === 0 || race.groupCompetitionId === 3
      );

      const playerTeamData = {
        selectedNewCyclists,
        selectedPreviousCyclist: selectedActualCyclists,
        cyclistsBonus,
        selectedRaces: [...obligatoryRaces, ...selectedRaces],
        bonusRaces,
        teamId,
      };

      fetch(`${apiUrl}/saveChangePlayerTeam`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(playerTeamData),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
          return response.json();
        })
        .then((data) => resolve(data))
        .catch((error) => {
          console.error("Error:", error);
          reject(error);
        });
    });
  };

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchModificationAvailable = async () => {
      try {
        const response = await fetch(`${apiUrl}/modificationTeamAvailable/`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération des données: ${response.status}`
          );
        }

        const data = await response.json();

        setModificationTeam1Available(data.changeTeam1Available);
        setModificationTeam2Available(data.changeTeam2Available);
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
      }
    };

    fetchModificationAvailable();
  }, [apiUrl, user, navigate]);

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchCyclists = async () => {
      try {
        const response = await fetch(`${apiUrl}/cyclists-list/${teamId}`);
        const data = await response.json();
        setCyclists(data);
      } catch (error) {
        console.error("Erreur lors de la récupération des cyclistes:", error);
      }
    };

    const fetchRaces = async () => {
      try {
        const response = await fetch(`${apiUrl}/races-list-period`);
        const data = await response.json();

        const formattedData = data.map((race) => ({
          ...race,
          date_start: new Date(race.date_start)
            .toLocaleDateString("en-GB")
            .replace(/\//g, "-"),
        }));

        setRaces(formattedData);
      } catch (error) {
        console.error("Erreur lors de la récupération des courses:", error);
      }
    };

    if (teamId) fetchCyclists();
    if (teamId === 1) fetchRaces();
  }, [apiUrl, user, navigate, teamId]);

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const calculateInitialBudget = (totalBudget) => {
      const totalValueOfSelectedCyclists = selectedCyclists.reduce(
        (total, selectedCyclist) => {
          const cyclist = cyclists.find(
            (c) => c.cyclistId === selectedCyclist.cyclistId
          );
          return total + (cyclist ? cyclist.finalValue : 0);
        },
        0
      );

      return totalBudget - totalValueOfSelectedCyclists;
    };

    const baseBudget = teamId === 1 ? 500 : 66;
    const initialBudget = calculateInitialBudget(baseBudget);
    setBudget(initialBudget);
  }, [apiUrl, user, navigate, selectedCyclists, cyclists, teamId]);

  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchTeamData = async () => {
      try {
        const response = await fetch(`${apiUrl}/userTeam/${teamId}`, {
          credentials: "include",
        });

        if (!response.ok) {
          throw new Error(
            `Erreur lors de la récupération des données de l'équipe: Statut ${response.status} - ${response.statusText}`
          );
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des données de l'équipe:",
          error
        );
        throw error;
      }
    };

    const loadData = async () => {
      try {
        const teamDataExist = await fetchTeamData();

        if (teamDataExist) {
          setSelectedCyclists(teamDataExist.selectedCyclists || []);
          setSelectedRaces(teamDataExist.selectedRaces || []);
          setBonusRaces(teamDataExist.bonusRaces || []);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des données:", error);
      }
    };

    if (teamId) loadData();
  }, [cyclists, apiUrl, user, navigate, teamId]);

  switch (step) {
    case 1:
      return (
        <div className="create-team">
          <div className="create-team-shadow-container">
            <div className="create-team-title">
              <h1>{t("changeTeam.step1.title")}</h1>
              <div className="create-team-shadow-mask-right"></div>
              <div className="create-team-shadow-mask-bottom"></div>
            </div>
          </div>

          <div className="create-team-container">
            <div className="create-team-left-container">
              <img
                src={worldTourBackground}
                alt={t("changeTeam.step1.worldTourAlt")}
                width="630"
                height="360"
                loading="lazy"
              />
              <button
                className="create-team-button"
                onClick={() => modifyTeam(1)}
                style={{
                  opacity: modificationTeam1Available ? "100%" : "40%",
                }}
                disabled={!modificationTeam1Available}
              >
                {t("changeTeam.step1.worldTour")}
              </button>
            </div>

            <div className="create-team-right-container">
              <img
                src={neoProBackground}
                alt={t("changeTeam.step1.neoProAlt")}
                width="550"
                height="360"
                loading="lazy"
              />
              <button
                className="create-team-button"
                onClick={() => modifyTeam(2)}
                style={{
                  opacity: modificationTeam2Available ? "100%" : "40%",
                }}
                disabled={!modificationTeam2Available}
              >
                {t("changeTeam.step1.neoPro")}
              </button>
            </div>
          </div>
        </div>
      );

    case 2:
      return (
        <SelectActualCyclists
          numberPlayerSelected={numberPlayerSelected}
          setNumberPlayerSelected={setNumberPlayerSelected}
          selectedCyclists={selectedCyclists}
          selectedActualCyclists={selectedActualCyclists}
          setSelectedActualCyclists={setSelectedActualCyclists}
          budget={budget}
          setBudget={setBudget}
          nextStep={nextStep}
        />
      );

    case 3:
      return (
        <SelectNewCyclists
          selectedCyclists={selectedCyclists}
          numberPlayerSelected={numberPlayerSelected}
          setNumberPlayerSelected={setNumberPlayerSelected}
          budget={budget}
          setBudget={setBudget}
          selectedNewCyclists={selectedNewCyclists}
          setSelectedNewCyclists={setSelectedNewCyclists}
          cyclists={cyclists}
          prevStep={prevStep}
          nextStep={nextStep}
        />
      );

    case 4:
      return (
        <SelectNewCyclistsBonus
          selectedCyclists={selectedCyclists}
          selectedActualCyclists={selectedActualCyclists}
          selectedNewCyclists={selectedNewCyclists}
          setCyclistsBonus={setCyclistsBonus}
          cyclistsBonus={cyclistsBonus}
          nextStep={nextStep}
          prevStep={prevStep}
          teamId={teamId}
          saveData={saveData}
        />
      );

    case 5:
      return (
        <SelectRaces
          selectedRaces={selectedRaces}
          setSelectedRaces={setSelectedRaces}
          races={races}
          setBonusRaces={setBonusRaces}
          bonusRaces={bonusRaces}
          nextStep={nextStep}
          prevStep={prevStep}
        />
      );

    case 6:
      return (
        <SelectBonusRace
          selectedRaces={selectedRaces}
          setBonusRaces={setBonusRaces}
          bonusRaces={bonusRaces}
          races={races}
          prevStep={prevStep}
          saveData={saveData}
        />
      );

    default:
      return <div>{t("changeTeam.errors.unknownStep")}</div>;
  }
};

export default ChangeTeamPage;
